import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Table, Statistic, Typography, Spin, DatePicker, message } from 'antd';
import { DollarCircleOutlined, ShoppingCartOutlined, BarChartOutlined } from '@ant-design/icons';
import api from '../../../api/axios';
import dayjs from 'dayjs';

const { Title,Text } = Typography;
const { RangePicker } = DatePicker;

const StatCard = ({ title, value, prefix, precision }) => (
  <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
    <Statistic
      title={title}
      value={value}
      prefix={prefix}
      precision={precision}
      valueStyle={{ fontWeight: 'bold', fontSize: 22 }}
    />
  </Card>
);

const SalesReport = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('startDate', dateRange[0].toISOString());
        params.append('endDate', dateRange[1].toISOString());
      }
      const { data } = await api.get(`/api/salesreports?${params.toString()}`);
      setStats(data);
    } catch (error) {
      console.error('Sales report fetch error:', error);
      messageApi.error('Failed to load sales report.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, messageApi]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const topProductsColumns = [
    { title: 'Part Name', dataIndex: 'partName', key: 'partName' },
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber' },
    { title: 'Quantity Sold', dataIndex: 'totalQuantitySold', align: 'right' },
    { title: 'Revenue', dataIndex: 'totalRevenue', align: 'right', render: (val) => `$${parseFloat(val).toFixed(2)}` },
  ];

  const topCustomersColumns = [
    { title: 'Customer Name', dataIndex: 'customerName', key: 'customerName' },
    { title: 'Total Spent', dataIndex: 'totalSpent', align: 'right', render: (val) => `$${parseFloat(val).toFixed(2)}` },
  ];

  const salesByShopColumns = [
    { title: 'Shop Name', dataIndex: 'shopName', key: 'shopName' },
    { title: 'Total Sales', dataIndex: 'totalSales', align: 'right', render: (val) => `$${parseFloat(val).toFixed(2)}` },
  ];

  const customerProductColumns = [
    { title: 'Customer', dataIndex: 'customerName', key: 'customerName' },
    { title: 'Part Name', dataIndex: 'partName', key: 'partName' },
    { title: 'Qty Bought', dataIndex: 'quantityBought', align: 'right' },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ padding: 24,  minHeight: '100vh' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3}>Sales Report</Title>
             <Text type="secondary">Analysis of sales report.</Text>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              allowClear
              placeholder={['Start date', 'End date']}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={8}>
            <StatCard title="Total Sales" value={stats?.kpis?.totalSales || 0} prefix={<DollarCircleOutlined />} precision={2} />
          </Col>
          <Col xs={24} md={8}>
            <StatCard title="Total Orders" value={stats?.kpis?.totalOrders || 0} prefix={<ShoppingCartOutlined />} />
          </Col>
          <Col xs={24} md={8}>
            <StatCard title="Average Order Value" value={stats?.kpis?.averageOrderValue || 0} prefix={<BarChartOutlined />} precision={2} />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Top Selling Products" bodyStyle={{ padding: 0 }}>
              <Table loading={loading} columns={topProductsColumns} dataSource={stats?.topSellingProducts} pagination={false} rowKey="partNumber" />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Top Customers" bodyStyle={{ padding: 0 }}>
              <Table loading={loading} columns={topCustomersColumns} dataSource={stats?.topCustomers} pagination={false} rowKey="customerName" />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Sales by Shop" bodyStyle={{ padding: 0 }}>
              <Table loading={loading} columns={salesByShopColumns} dataSource={stats?.salesByShop} pagination={false} rowKey="shopName" />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Customers by Product" bodyStyle={{ padding: 0 }}>
              <Table loading={loading} columns={customerProductColumns} dataSource={stats?.customerProductStats} pagination={false} rowKey={(r) => `${r.customerName}-${r.partName}`} />
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SalesReport;
